import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AlertsFilterCategory, AlertsFilters } from '@sternum/web/features/alerts/models';
import { AnomaliesFilterCategory, AnomaliesFilters } from '@sternum/web/features/anomalies/models';
import { AttackFilters, AttackGroupByField } from '@sternum/web/features/attacks/models';
import { appStorage } from '@sternum/web/services/appStorage';
import { updateFiltersPerCategory, UpdateFiltersPerCategoryArgs } from '@sternum/web/utils';

interface TabLogs {
  expandedRows: Record<string, boolean>;
  isFiltersPanelOpen: boolean;
}

interface TabAlerts {
  searchValue: string;
  isSidebarFilterOpen: boolean;
  selectedFiltersPerCategory: AlertsFilters;
}

interface TabAnomalies {
  searchValue: string;
  isSidebarFilterOpen: boolean;
  selectedFiltersPerCategory: AnomaliesFilters;
}

interface TabAttacks {
  searchValue: string;
  isSidebarFilterOpen: boolean;
  selectedFiltersPerCategory: AttackFilters;
}

export interface DeviceDetailsState {
  isPolling: boolean;
  tabAnomalies: TabAnomalies;
  tabLogs: TabLogs;
  tabAlerts: TabAlerts;
  tabAttacks: TabAttacks;
}

const getInitialState: () => DeviceDetailsState = () => {
  return {
    isPolling: false,
    tabAnomalies: {
      searchValue: '',
      isSidebarFilterOpen: appStorage.device.getIsSidebarFilterOpenForTabAnomalies() ?? false,
      selectedFiltersPerCategory: {},
    },
    tabLogs: {
      expandedRows: {},
      isFiltersPanelOpen: appStorage.device.getIsSidebarFilterOpenForTabLogs() ?? false,
    },
    tabAlerts: {
      searchValue: '',
      isSidebarFilterOpen: appStorage.deviceDetails.getIsSidebarFilterOpenForTabAlerts() ?? false,
      selectedFiltersPerCategory: {},
    },
    tabAttacks: {
      searchValue: '',
      isSidebarFilterOpen: appStorage.deviceDetails.getIsSidebarFilterOpenForTabAttacks() ?? false,
      selectedFiltersPerCategory: {},
    },
  };
};

export const deviceDetailsSlice = createSlice({
  name: 'deviceDetails',
  initialState: getInitialState,
  reducers: {
    clearDeviceDetailsStateAction() {
      return getInitialState();
    },

    toggleIsPollingAction(state, action: PayloadAction<boolean | undefined>) {
      state.isPolling = action.payload === undefined ? !state.isPolling : action.payload;
    },

    tabAnomaliesSetSearchValueAction(state, action: PayloadAction<string>) {
      state.tabAnomalies.searchValue = action.payload;
      state.isPolling = false;
    },

    tabAnomaliesToggleIsSidebarFilterOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.tabAnomalies.isSidebarFilterOpen =
        action.payload === undefined ? !state.tabAnomalies.isSidebarFilterOpen : action.payload;
    },

    tabAnomaliesUpdateFiltersPerCategoryAction(
      state,
      action: PayloadAction<UpdateFiltersPerCategoryArgs<AnomaliesFilterCategory>>,
    ) {
      const categoryKey = action.payload.categoryKey;

      state.tabAnomalies.selectedFiltersPerCategory[categoryKey] = updateFiltersPerCategory<AnomaliesFilterCategory>(
        state.tabAnomalies.selectedFiltersPerCategory[categoryKey] || {},
        action.payload,
      );
      state.isPolling = false;
    },

    tabLogsToggleRowAction: function (state, action: PayloadAction<string>) {
      const rowId = action.payload;
      const isRowExpanded = state.tabLogs.expandedRows[rowId];

      if (isRowExpanded) {
        delete state.tabLogs.expandedRows[rowId];
      } else {
        state.tabLogs.expandedRows[action.payload] = true;
      }

      state.isPolling = state.isPolling && !Object.values(state.tabLogs.expandedRows).some((expanded) => expanded);
    },

    tabLogsCollapseAllAction(state) {
      state.tabLogs.expandedRows = {};
    },

    tabLogsToggleIsFiltersPanelOpen(state) {
      state.tabLogs.isFiltersPanelOpen = !state.tabLogs.isFiltersPanelOpen;
    },

    tabAlertsSetSearchValueAction(state, action: PayloadAction<string>) {
      state.tabAlerts.searchValue = action.payload;
      state.isPolling = false;
    },

    tabAlertsToggleIsSidebarFilterOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.tabAlerts.isSidebarFilterOpen =
        action.payload === undefined ? !state.tabAlerts.isSidebarFilterOpen : action.payload;
    },

    tabAlertsUpdateFiltersPerCategoryAction(
      state,
      action: PayloadAction<UpdateFiltersPerCategoryArgs<AlertsFilterCategory>>,
    ) {
      const categoryKey = action.payload.categoryKey;

      state.tabAlerts.selectedFiltersPerCategory[categoryKey] = updateFiltersPerCategory<AlertsFilterCategory>(
        state.tabAlerts.selectedFiltersPerCategory[categoryKey] || {},
        action.payload,
      );
      state.isPolling = false;
    },

    tabAnomaliesClearAllFiltersAction(state) {
      state.tabAnomalies.selectedFiltersPerCategory = {};
      state.isPolling = false;
    },

    tabAlertsClearAllFiltersAction(state) {
      state.tabAlerts.selectedFiltersPerCategory = {};
      state.isPolling = false;
    },

    tabAttacksSetSearchValueAction(state, action: PayloadAction<string>) {
      state.tabAttacks.searchValue = action.payload;
      state.isPolling = false;
    },

    tabAttacksToggleIsSidebarFilterOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.tabAttacks.isSidebarFilterOpen =
        action.payload === undefined ? !state.tabAttacks.isSidebarFilterOpen : action.payload;
    },

    tabAttacksUpdateFiltersPerCategoryAction(
      state,
      action: PayloadAction<UpdateFiltersPerCategoryArgs<AttackGroupByField>>,
    ) {
      const categoryKey = action.payload.categoryKey;

      state.tabAttacks.selectedFiltersPerCategory[categoryKey] = updateFiltersPerCategory<AttackGroupByField>(
        state.tabAttacks.selectedFiltersPerCategory[categoryKey] || {},
        action.payload,
      );
      state.isPolling = false;
    },

    tabAttacksClearAllFiltersAction(state) {
      state.tabAttacks.selectedFiltersPerCategory = {};
      state.isPolling = false;
    },
  },
});

export const {
  clearDeviceDetailsStateAction,
  toggleIsPollingAction,
  tabAnomaliesSetSearchValueAction,
  tabAnomaliesToggleIsSidebarFilterOpenAction,
  tabAnomaliesUpdateFiltersPerCategoryAction,
  tabAnomaliesClearAllFiltersAction,
  tabLogsCollapseAllAction,
  tabLogsToggleIsFiltersPanelOpen,
  tabLogsToggleRowAction,
  tabAlertsSetSearchValueAction,
  tabAlertsToggleIsSidebarFilterOpenAction,
  tabAlertsUpdateFiltersPerCategoryAction,
  tabAlertsClearAllFiltersAction,
  tabAttacksSetSearchValueAction,
  tabAttacksToggleIsSidebarFilterOpenAction,
  tabAttacksUpdateFiltersPerCategoryAction,
  tabAttacksClearAllFiltersAction,
} = deviceDetailsSlice.actions;
